import React, { useCallback, useEffect, useState } from 'react';

import { apiPatch } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { isAvCallingEnabled } from '../../lib/workspace';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { SettingsGroup, SettingsToggle } from './SettingsList';

// Optimistic save with revert on failure; other clients sync via the journal.
const useWorkspaceFlag = (workspace, field, errorMessage, readValue = (record, key) => Boolean(record?.[key])) => {
  const workspaceId = workspace?.id;
  const workspaceValue = readValue(workspace, field);
  const [enabled, setEnabled] = useState(workspaceValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setEnabled(workspaceValue);
  }, [workspaceId, workspaceValue]);

  const handleChange = useCallback(
    async (next) => {
      if (!workspaceId) return;

      setEnabled(next);
      setError(null);
      setIsSaving(true);

      try {
        const response = await apiPatch(`/workspaces/${workspaceId}`, { workspace: { [field]: next } });
        const data = await response.json();
        if (!response.ok || data.ok !== true) {
          setEnabled(!next);
          setError(errorMessage);
        }
      } catch (saveError) {
        setEnabled(!next);
        setError(errorMessage);
        logError('BetaFeaturesPanel', `Failed to save ${field}`, { error: saveError });
      } finally {
        setIsSaving(false);
      }
    },
    [errorMessage, field, workspaceId],
  );

  return { enabled, isSaving, error, handleChange };
};

// The "Beta Features" section page: opt-in workspace functionality. Each toggle
// strictly gates its feature's UI.
const BetaFeaturesPanel = () => {
  const { workspace, profile } = useRouteWorkspace();
  const isAdmin = profile?.role === 'admin';
  const channels = useWorkspaceFlag(workspace, 'channels_enabled', 'Unable to save channel settings.');
  // Video chat reads through isAvCallingEnabled rather than the bare column: it defaults
  // on, so a workspace row cached before the field existed must not read as off.
  const videoChat = useWorkspaceFlag(workspace, 'av_calling_enabled', 'Unable to save video chat settings.', isAvCallingEnabled);

  if (!workspace || !profile || !isAdmin) return null;

  return (
    <div className="w-full pt-10">
      <SettingsGroup>
        <SettingsToggle
          label="Channels"
          description="Create channels within this chat"
          checked={channels.enabled}
          disabled={channels.isSaving}
          onChange={channels.handleChange}
        />
        <SettingsToggle
          label="Video chat"
          description="Start video calls in this chat."
          checked={videoChat.enabled}
          disabled={videoChat.isSaving}
          onChange={videoChat.handleChange}
        />
      </SettingsGroup>
      <span className="mt-2 block min-h-4 text-left text-xs text-red-700 dark:text-red-300" aria-live="polite">
        {channels.error || videoChat.error}
      </span>
    </div>
  );
};

export default BetaFeaturesPanel;
